import { Pencil, Trash2, Copy } from "lucide-react";
import { Link, useParams } from "react-router-dom";

const JobsTable = ({
  data = [],
  selectedJobs = [],
  setSelectedJobs,
  onDelete,
  onClone,
  onEdit,
}) => {
  const { id: projectId } = useParams();

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Draft":
        return "bg-gray-100 text-gray-700";
      case "Offered to Vendor":
        return "bg-purple-100 text-purple-700";
      case "Offer Accepted":
        return "bg-green-100 text-green-700";
      case "Offer Rejected":
        return "bg-danger-100 text-danger-700";
      case "Started":
        return "bg-brand-100 text-brand-700";
      case "Completed":
        return "bg-teal-100 text-teal-700";
      case "Hold":
        return "bg-warning-100 text-warning-700";
      case "Completion Accepted":
        return "bg-green-100 text-green-700";
      case "Completion Rejected":
        return "bg-danger-100 text-danger-700";
      case "Cancelled":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedJobs(data.map((job) => job.id));
    } else {
      setSelectedJobs([]);
    }
  };

  const handleSelectJob = (jobId) => {
    if (selectedJobs.includes(jobId)) {
      setSelectedJobs(selectedJobs.filter((id) => id !== jobId));
    } else {
      setSelectedJobs([...selectedJobs, jobId]);
    }
  };

  const HEADERS = [
    "ID",
    "Name",
    "Service",
    "Language Pair",
    "Deadline At",
    "Auto Start",
    "Prev Job",
    "Next Job",
    "Vendor",
    "Status",
    "Started At",
    "Completed At",
    "Payables (INR)",
    "Actions",
  ];

  const COL_WIDTHS = ["3%","5%","12%","8%","10%","10%","5%","5%","5%","10%","8%","10%","10%","8%","5%"];

  return (
    <div className="w-full bg-white shadow rounded-lg overflow-hidden">
      <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
        <table className="w-full table-auto border-collapse min-w-[1500px]">
          <colgroup>
            {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
          </colgroup>

          <thead className="sticky top-0 z-10">
            <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
              <th className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={data.length > 0 && selectedJobs.length === data.length}
                  onChange={handleSelectAll}
                  className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                />
              </th>
              {HEADERS.map((h) => (
                <th key={h} className="px-3 py-2 text-center font-semibold whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length + 1} className="py-10 text-center text-gray-400 text-xs">
                  No jobs found.
                </td>
              </tr>
            ) : (
              data.map((row, index) => {
                const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                return (
                  <tr key={row.id} className={`${rowBg} hover:bg-brand-50 transition-colors`}>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={selectedJobs.includes(row.id)}
                        onChange={() => handleSelectJob(row.id)}
                        className="w-3.5 h-3.5 accent-brand-600 cursor-pointer"
                      />
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                      <Link
                        to={`/project/${projectId}/job/${row.id}`}
                        className="text-brand-600 hover:text-brand-800 font-medium underline"
                      >
                        J{row.id}
                      </Link>
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-900">
                      {row.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.service?.name || "—"}
                    </td>

                    <td className="px-3 py-2 text-xs break-words text-gray-700">
                      {row.languagePair
                        ? `${row.languagePair.sourceLanguage?.name || ""} → ${row.languagePair.targetLanguage?.name || ""}`
                        : "—"}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.deadline_at)}
                    </td>

                    <td className="px-3 py-2 text-xs text-center">
                      {row.auto_start_on_vendor_acceptance ? (
                        <span className="inline-block w-3 h-3 bg-emerald-500 rounded-full" title="Yes" />
                      ) : (
                        <span className="inline-block w-3 h-3 bg-gray-300 rounded-full" title="No" />
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs text-center whitespace-nowrap">
                      {row.previous_job_id ? (
                        <Link
                          to={`/project/${projectId}/job/${row.previous_job_id}`}
                          className="text-brand-600 hover:text-brand-800 underline font-medium"
                        >
                          J{row.previous_job_id}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs text-center whitespace-nowrap">
                      {row.next_job_id ? (
                        <Link
                          to={`/project/${projectId}/job/${row.next_job_id}`}
                          className="text-brand-600 hover:text-brand-800 underline font-medium"
                        >
                          J{row.next_job_id}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-3 py-2 text-xs break-words">
                      {row.vendor ? (
                        <Link
                          to={`/vendors/${row.vendor.id}`}
                          className="text-brand-600 hover:text-brand-800 underline font-medium"
                        >
                          {row.vendor.company_name ||
                            `${row.vendor.primary_users?.first_name || ""} ${
                              row.vendor.primary_users?.last_name || ""
                            }`.trim() ||
                            "—"}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <span className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded ${getStatusColor(row.status)}`}>
                        {row.status}
                      </span>
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.started_at)}
                    </td>

                    <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-500">
                      {formatDateTime(row.completed_at)}
                    </td>

                    <td className="px-3 py-2 text-xs font-semibold text-right whitespace-nowrap text-gray-900">
                      {row.payables_amount
                        ? `₹${parseFloat(row.payables_amount).toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        : "—"}
                    </td>

                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      <div className="flex justify-center gap-3">
                        <Copy
                          className="w-4 h-4 text-gray-400 hover:text-gray-700 cursor-pointer transition-colors"
                          title="Clone Job"
                          onClick={() => onClone(row.id)}
                        />
                        <Pencil
                          className="w-4 h-4 text-brand-400 hover:text-brand-600 cursor-pointer transition-colors"
                          title="Edit Job"
                          onClick={() => onEdit(row.id)}
                        />
                        <Trash2
                          className="w-4 h-4 text-danger-400 hover:text-danger-600 cursor-pointer transition-colors"
                          title="Delete Job"
                          onClick={() => onDelete(row.id)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JobsTable;
